import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import permanentDstPlugin from '../../../../ts-utils/dayjs-permanent-dst'
import {
  rebaseInstantToZone,
  type RebaseInstantDeps,
} from '../../../event/rebaseInstantToZone'

// `.tz()` below needs these. Idempotent, and registering here keeps this module
// standalone rather than relying on another file's import side effect.
dayjs.extend(utc)
dayjs.extend(timezone)
// Keeps a zone that abolished its clock change reading — and WRITING — on the
// offset it actually keeps. Both directions below go through `.tz()`, which is
// the seam this patches. See ts-utils/dayjs-permanent-dst.
dayjs.extend(permanentDstPlugin)

/**
 * The game form's dayjs binding for the shared wall-clock rebase rule.
 *
 * `common/features/event/rebaseInstantToZone` owns the RULE — hold the wall
 * clock, change the zone — and is framework- and feature-agnostic despite where
 * it sits; only the date-library conversion is supplied here.
 *
 * In `common/` rather than in each platform's feature folder, and that
 * placement is load-bearing on MOBILE. Two dayjs copies exist — the root's
 * 1.11.11 and mobile's own 1.10.5 — and `registerPermanentDst` extends the
 * root, because it lives here. A binding under `mobile/src` resolves mobile's
 * copy instead, which the plugin never patched: that is exactly why the event
 * feature's own mobile binding cannot be reused for this, and why a Vancouver
 * game after 2026-11-01 would rebase onto the wrong offset if it were.
 *
 * The event feature keeps its two platform bindings; merging them means moving
 * files it depends on, which is a refactor of its own.
 */
const deps: RebaseInstantDeps = {
  readWallClock: (iso, zone) => {
    const zoned = dayjs.utc(iso).tz(zone)

    if (!zoned.isValid()) {
      return null
    }

    return {
      year: zoned.year(),
      // dayjs months are 0-indexed; the shared shape uses calendar months.
      month: zoned.month() + 1,
      day: zoned.date(),
      hour: zoned.hour(),
      minute: zoned.minute(),
      second: zoned.second(),
    }
  },
  writeWallClock: (wallClock, zone) => {
    const pad = (value: number) => String(value).padStart(2, '0')
    const formatted =
      `${wallClock.year}-${pad(wallClock.month)}-${pad(wallClock.day)}` +
      `T${pad(wallClock.hour)}:${pad(wallClock.minute)}:${pad(
        wallClock.second
      )}`

    return dayjs.tz(formatted, zone).toISOString()
  },
}

/**
 * The game's start re-interpreted so the clock the admin typed against
 * `fromZone` reads identically against `toZone`.
 *
 * Called when the LOCATION changes: the admin picked "9:15 PM at this venue",
 * so moving the game to a venue in another zone keeps 9:15 PM and moves the
 * instant. That is what the create path already does — it re-zones the typed
 * clock into whichever venue is selected at submit — so edit now agrees with
 * it, and with the abbreviation shown beside the Start Date label.
 *
 * Empty values, a missing zone on either side, and matching zones pass through
 * untouched, so callers can apply it unconditionally.
 */
export const rebaseGameZone = (
  iso: string,
  fromZone: string,
  toZone: string
): string => rebaseInstantToZone(iso, fromZone, toZone, deps)
